import React, { useEffect, useMemo, useState } from 'react';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import { useDataGrid } from '../../../components/devexpress/Grid/dx-grid.ts';
import {
  BooleanParam,
  DateParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import {
  AdvancedFormReturnType,
  useAdvancedForm,
} from '../../../helpers/form/useAdvancedForm.ts';
import { add, sub } from 'date-fns';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import clsx from 'clsx';
import styles from './OrderList.module.scss';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import { OrderListQuery } from 'services/api/api-client.ts';
import { Accordion } from './components/Accordion.tsx';
import { useWatch } from 'react-hook-form';
import { Filters } from './components/Filters.tsx';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { UseTranslationResponse } from 'react-i18next';
import { AccordionHeader } from '../../../components/uikit/AccordionHeader.tsx';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';
import { abasUnavailableUrn, ErrObj } from '../../../helpers/abas-helper.ts';

export type FormParams = {
  ordersAmountOfPeriods: number | null;
  ordersPeriodType: string;
  deliveryPeriodDirection: string;
  deliveryAmountOfPeriods: number | null;
  deliveryPeriodType: string;
  orderNumber: string;
  reference: string;
  deliveryDateFrom: Date;
  deliveryDateTo: Date;
  orderedFrom: Date;
  orderedTo: Date;
};

export type PaginationProps = {
  page: number;
  perPage: number;
  totalCount: number;
  pageSizes: number[];
  changePagination: (pagination: { page?: number; perPage?: number }) => void;
};

export type SelectState = {
  ordersPeriodType: boolean;
  deliveryPeriodType: boolean;
  deliveryPeriodDirection: boolean;
};

export type FilterProps = {
  form: AdvancedFormReturnType<FormParams>;
  reference: string;
  selectState: SelectState;
  periodOptions: string[];
  directionOptions: string[];
  ordersPeriodType: string;
  ordersAmountOfPeriods: number | null;
  orderNumber: string;
  deliveryAmountOfPeriods: number | null;
  deliveryPeriodDirection: string;
  deliveryPeriodType: string;
  isResetButtonShow: boolean;
  resetFilters: () => void;
  changeSelectionState: (change: {
    [key in keyof SelectState]?: boolean;
  }) => void;
};

const pageSizes = [10, 20, 50];
const columnsOfOuterTable: {
  translateKey: Parameters<
    UseTranslationResponse<'translation', 'Page.OrderList'>['t']
  >[0];
  serverKey: string;
  width: number | string;
  classname?: string;
  isSortingTurnedOff?: boolean;
}[] = [
  {
    serverKey: 'order',
    translateKey: 'order',
    width: 132,
    classname: styles.paddingLeft6,
  },
  {
    serverKey: 'from',
    translateKey: 'from',
    width: 124,
    classname: styles.paddingLeft6,
  },
  {
    serverKey: 'yourOrder',
    translateKey: 'your_order',
    width: 'unset',
    classname: clsx(styles.flex1, styles.paddingLeft6),
  },
  {
    serverKey: 'yourReference',
    translateKey: 'your_reference',
    width: 'unset',
    classname: clsx(styles.flex1, styles.paddingLeft12),
  },
  {
    serverKey: 'deliveryDate',
    translateKey: 'delivery_date_earliest',
    width: 220,
    classname: styles.paddingLeft12,
  },
  {
    serverKey: 'quantity',
    translateKey: 'quantity',
    width: 112,
  },
];

export const OrderList = () => {
  const i18n = useScopedTranslation('Page.OrderList');
  const [openedOrders, setOpenedOrders] = useState<Set<string>>(new Set([]));
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null);
  const [selectState, setSelectState] = useState<SelectState>({
    ordersPeriodType: false,
    deliveryPeriodType: false,
    deliveryPeriodDirection: false,
  });

  const periodOptions = ['year', 'month', 'week', 'day'];

  const directionOptions = ['next', 'last'];

  useDataGrid();
  const [params, setParams] = useQueryParams({
    ordersAmountOfPeriods: NumberParam,
    ordersPeriodType: StringParam,
    orderedTo: DateParam,
    orderedFrom: DateParam,
    deliveryPeriodDirection: StringParam,
    deliveryAmountOfPeriods: NumberParam,
    deliveryPeriodType: StringParam,
    deliveryDateFrom: DateParam,
    deliveryDateTo: DateParam,
    orderNumber: StringParam,
    reference: StringParam,
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'deliveryDate'),
    desc: withDefault(BooleanParam, false),
  });

  const form = useAdvancedForm<FormParams>(
    async (data) => {
      let deliveryDateFrom: Date | undefined =
        deliveryPeriodDirection === directionOptions[1] &&
        deliveryAmountOfPeriods
          ? sub(new Date(), {
              [deliveryPeriodType + 's']: deliveryAmountOfPeriods,
            })
          : new Date();

      let deliveryDateTo: Date | undefined =
        deliveryPeriodDirection === directionOptions[0] &&
        deliveryAmountOfPeriods
          ? add(new Date(), {
              [deliveryPeriodType + 's']: deliveryAmountOfPeriods,
            })
          : new Date();

      if (!deliveryAmountOfPeriods) {
        deliveryDateFrom = undefined;
        deliveryDateTo = undefined;
      }

      let orderedFrom: Date | undefined = sub(new Date(), {
        [ordersPeriodType + 's']: ordersAmountOfPeriods,
      });

      let orderedTo: Date | undefined = new Date();

      if (!ordersAmountOfPeriods) {
        orderedFrom = undefined;
        orderedTo = undefined;
      }

      setParams({
        ...params,
        ...data,
        page: 1,
        deliveryDateFrom,
        deliveryDateTo,
        orderedFrom,
        orderedTo,
      });
    },
    {
      defaultValues: {
        orderNumber: params.orderNumber ?? '',
        reference: params.reference ?? '',
        ordersPeriodType: params.ordersPeriodType ?? periodOptions[0],
        ordersAmountOfPeriods: params.ordersAmountOfPeriods ?? null,
        deliveryPeriodDirection:
          params.deliveryPeriodDirection ?? directionOptions[0],
        deliveryAmountOfPeriods: params.deliveryAmountOfPeriods ?? null,
        deliveryPeriodType: params.deliveryPeriodType ?? periodOptions[2],
        ...pagingSortingToBackendRequest(params),
      },
    },
  );

  const [
    deliveryAmountOfPeriods,
    ordersAmountOfPeriods,
    ordersPeriodType,
    deliveryPeriodType,
    deliveryPeriodDirection,
    orderNumber,
    reference,
  ] = useWatch({
    control: form.control,
    name: [
      'deliveryAmountOfPeriods',
      'ordersAmountOfPeriods',
      'ordersPeriodType',
      'deliveryPeriodType',
      'deliveryPeriodDirection',
      'orderNumber',
      'reference',
    ],
  });

  const orderListQuery = OrderListQuery.useGetQuery(
    {
      order: params.orderNumber,
      reference: params.reference,
      deliveryDateFrom: params.deliveryDateFrom,
      deliveryDateTo: params.deliveryDateTo,
      orderedFrom: params.orderedFrom,
      orderedTo: params.orderedTo,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );
  const data = orderListQuery.data;
  const totalCount = data?.countOfRecords ?? 0;

  const handleClickAccordion = (key: string) => {
    const copy = new Set(openedOrders);

    if (copy.has(key)) {
      copy.delete(key);
    } else {
      copy.add(key);
    }

    setOpenedOrders(copy);
  };

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const paginationProps: PaginationProps = useMemo(() => {
    return {
      page: params.page,
      perPage: params.perPage,
      totalCount: totalCount ?? 0,
      pageSizes: pageSizes,
      changePagination: changePagination,
    };
  }, [params, totalCount]);

  const changeSortTable = (key: string) => {
    const previousKey = params.sortBy;

    setParams((prevParams) => ({
      ...prevParams,
      sortBy: key,
      desc: previousKey === key ? !params.desc : false,
      page: 1,
    }));
  };

  useEffect(() => {
    const closeFunc = () => {
      setSelectState({
        ordersPeriodType: false,
        deliveryPeriodDirection: false,
        deliveryPeriodType: false,
      });
    };

    document.body.onscroll = closeFunc;

    return () => {
      document.body.onscroll = null;
    };
  }, []);

  const resetFilters = () => {
    form.reset({
      orderNumber: '',
      reference: '',
      deliveryAmountOfPeriods: null,
      deliveryPeriodDirection: directionOptions[0],
      deliveryPeriodType: periodOptions[2],
      ordersAmountOfPeriods: null,
      ordersPeriodType: periodOptions[0],
    });

    void form.handleSubmitDefault();
  };

  const isResetButtonShow =
    !!params.orderNumber ||
    !!params.reference ||
    !!params.deliveryAmountOfPeriods ||
    !!params.ordersAmountOfPeriods;

  const changeSelectionState = (change: {
    [key in keyof SelectState]?: boolean;
  }) => {
    setSelectState((prevState: SelectState) => ({
      ...prevState,
      ...change,
    }));
  };

  const filterProps: FilterProps = {
    form,
    selectState,
    changeSelectionState,
    directionOptions,
    deliveryPeriodDirection,
    deliveryPeriodType,
    periodOptions,
    ordersPeriodType,
    isResetButtonShow,
    resetFilters,
    ordersAmountOfPeriods,
    deliveryAmountOfPeriods,
    orderNumber,
    reference,
  };

  const errObj = orderListQuery?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !data?.records.length &&
    !orderListQuery.isLoading &&
    !orderListQuery.isPlaceholderData &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'order-list-container'}>
      <Breadcrumbs />
      <Loading
        loading={orderListQuery.isLoading || orderListQuery.isPlaceholderData}
      >
        <Filters {...filterProps} />
        {!isExternalServiceUnavailable && (
          <>
            <AccordionHeader
              desc={params.desc}
              sortBy={params.sortBy}
              i18n={i18n}
              columnsOfOuterTable={columnsOfOuterTable}
              hoveredHeader={hoveredHeader}
              changeSortTable={changeSortTable}
              setHoveredHeader={(value) => setHoveredHeader(value)}
            />
            <Accordion
              handleClickAccordion={handleClickAccordion}
              paginationProps={paginationProps}
              openedOrders={openedOrders}
              data={data?.records ?? []}
              isDesc={params.desc}
            />
          </>
        )}
        {isPlaceholderVisible && <TablePlaceholder testId={'placeholder'} />}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        <AppPagination {...paginationProps} />
      </Loading>
    </div>
  );
};
